import Head from "next/head";
import Script from "next/script";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "@/components/Image";
import {
  COMPLETE_VIDEO,
  PLAY_25_VIDEO,
  PLAY_50_VIDEO,
  PLAY_75_VIDEO,
  PLAY_VIDEO,
} from "@/constants/amplitude";
import { JW_PLAYER_BASE, JW_PLAYER_LIBRARY } from "@/constants/media";
import { ArticleHero, Maybe } from "@/graphql/gql/graphql";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import isClient from "@/utils/isClient";

type HeroVideoProps = { title: string; hero?: Maybe<ArticleHero> };

interface TimeArgsT {
  duration: number;
  currentTime: number;
}

const HeroVideo = ({ title, hero }: HeroVideoProps) => {
  const [error, setError] = useState(false);
  const { logAmplitudeEvent } = useAmplitude();
  const videoRef = useRef<HTMLDivElement>(null);

  const onComplete = useCallback(() => {
    const videoTitle = window.jwplayer().getPlaylistItem()?.title || "";
    const duration = window.jwplayer().getDuration();
    logAmplitudeEvent(COMPLETE_VIDEO, {
      module: "video",
      "Article Name": title,
      "Video Title": videoTitle,

      "Video Played Duration": duration,
      "Total Video Duration": duration,
    });
  }, [title, logAmplitudeEvent]);

  const onPlay = useCallback(() => {
    const videoTitle = window.jwplayer().getPlaylistItem()?.title || "";
    logAmplitudeEvent(PLAY_VIDEO, {
      "Total Video Duration": window.jwplayer().getDuration(),
      module: "video",
      "Article Name": title,
      "Video Title": videoTitle,
    });
  }, [title, logAmplitudeEvent]);
  const timeRef = useRef<string>();

  const onTime = useCallback(
    ({ duration: videoDuration, currentTime }: TimeArgsT) => {
      const duration = window.jwplayer().getDuration();
      const percentage = (currentTime / videoDuration) * 100;
      if (percentage < 25) {
        return;
      }
      let eventType = "";
      if (percentage >= 25) {
        eventType = PLAY_25_VIDEO;
      }
      if (percentage >= 50) {
        eventType = PLAY_50_VIDEO;
      }
      if (percentage >= 75) {
        eventType = PLAY_75_VIDEO;
      }
      if (eventType !== timeRef.current) {
        const videoTitle = window.jwplayer().getPlaylistItem()?.title || "";
        logAmplitudeEvent(eventType, {
          "Video Played Duration": currentTime,
          "Total Video Duration": duration,
          module: "video",
          "Article Name": title,
          "Video Title": videoTitle,
        });
        timeRef.current = eventType;
      }
    },
    [title, logAmplitudeEvent]
  );

  const jwScript = useCallback(() => {
    if (window.jwplayer && videoRef.current && isClient()) {
      window.jwplayer(videoRef.current).remove();
      window.jwplayer(videoRef.current).setup({
        playlist: `${JW_PLAYER_BASE}/v2/media/${hero?.jwPlayerId}`,
        width: "100%",
        aspectratio: "16:9",
        generateSEOMetadata: true,
        image: `${JW_PLAYER_BASE}/v2/media/${hero?.jwPlayerId}/poster.jpg?width=720`,
      });

      window.jwplayer().on("setupError", () => setError(true));
      window.jwplayer().on("meta", () => {
        window.jwplayer().on("time", onTime);
        window.jwplayer().on("play", onPlay);
        window.jwplayer().on("complete", onComplete);
      });
    }
  }, [hero?.jwPlayerId, onTime, onPlay, onComplete]);

  useEffect(() => {
    jwScript();
  }, [jwScript]);

  return (
    <>
      <Script
        id="jw-player"
        strategy="afterInteractive"
        src={`${JW_PLAYER_BASE}/libraries/${JW_PLAYER_LIBRARY}.js`}
        onLoad={jwScript}
      />
      <Head>
        <link
          rel="preload"
          as="image"
          href={`${JW_PLAYER_BASE}/v2/media/${hero?.jwPlayerId}/poster.jpg?width=480`}
        />
        <link
          rel="preload"
          as="image"
          href={`${JW_PLAYER_BASE}/v2/media/${hero?.jwPlayerId}/poster.jpg?width=1280`}
        />
      </Head>
      {error ? (
        <Image
          priority
          width={964}
          height={477}
          sizes="(max-width: 960px) 50vw, 100vw"
          alt={title}
          src={hero?.imageUrl?.url}
          style={{ width: "100%", height: "auto" }}
        />
      ) : (
        <div data-testid="video" style={{ minHeight: "5em" }} ref={videoRef} />
      )}
    </>
  );
};

export default HeroVideo;
